import { Pencil, Plus, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router";

import type { WardCreateInput } from "@handoverpro/shared";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import type { AdminWardDto } from "~/lib/api";
import { archiveWard, createWard, fetchWards, updateWard } from "~/lib/wards";

import { ArchiveWardDialog } from "./archive-ward-dialog";
import { WardFormDialog } from "./ward-form-dialog";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; wards: AdminWardDto[] };

// The open form dialog: creating a new ward, or editing an existing one.
type FormState = { mode: "create" } | { mode: "edit"; ward: AdminWardDto };

const EMPTY_WARD: WardCreateInput = { name: "", shortCode: "" };

/**
 * Super-admin ward management (V2-E1.2, HAN-86): list every ward (with an
 * archived toggle), create, edit, and archive. Mutations refetch the list so the
 * screen always reflects the server. The route (`/admin/wards`) gates this to
 * super-admins; the API enforces the same on every call.
 */
export function WardManagementScreen(): JSX.Element {
  const [state, setState] = useState<State>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [showArchived, setShowArchived] = useState(false);

  const [formState, setFormState] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [archiveTarget, setArchiveTarget] = useState<AdminWardDto | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const load = useCallback(async (signal: { cancelled: boolean }): Promise<void> => {
    setState({ kind: "loading" });
    const result = await fetchWards();
    if (signal.cancelled) return;
    if (result.status === "error") {
      setState({ kind: "error", message: result.message });
      return;
    }
    setState({ kind: "ready", wards: result.data });
  }, []);

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  const refetch = (): void => setReloadKey((k) => k + 1);

  const openCreate = (): void => {
    setSaveError(null);
    setFormState({ mode: "create" });
  };

  const openEdit = (ward: AdminWardDto): void => {
    setSaveError(null);
    setFormState({ mode: "edit", ward });
  };

  const onSubmitForm = async (values: WardCreateInput): Promise<void> => {
    if (!formState) return;
    setSaveError(null);
    const result =
      formState.mode === "create"
        ? await createWard(values)
        : await updateWard(formState.ward.id, values);
    if (result.status === "error") {
      setSaveError(result.message);
      return;
    }
    setFormState(null);
    refetch();
  };

  const onConfirmArchive = async (): Promise<void> => {
    if (!archiveTarget) return;
    setArchiving(true);
    setArchiveError(null);
    const result = await archiveWard(archiveTarget.id);
    setArchiving(false);
    if (result.status === "error") {
      setArchiveError(result.message);
      return;
    }
    setArchiveTarget(null);
    refetch();
  };

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Wards</h1>
          <p className="text-sm text-muted-foreground">
            Create and manage the wards doctors hand over on.
          </p>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden />
          Add ward
        </Button>
      </header>

      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={showArchived}
          onChange={(e) => setShowArchived(e.target.checked)}
          className="h-4 w-4 rounded border-border accent-primary"
        />
        Show archived wards
      </label>

      {state.kind === "loading" && (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading wards">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 animate-pulse rounded-xl border bg-muted/40" />
          ))}
        </div>
      )}

      {state.kind === "error" && (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>{state.message}</span>
            <Button variant="outline" size="sm" onClick={refetch}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {state.kind === "ready" && (
        <WardList
          wards={state.wards}
          showArchived={showArchived}
          onEdit={openEdit}
          onArchive={(ward) => {
            setArchiveError(null);
            setArchiveTarget(ward);
          }}
        />
      )}

      <WardFormDialog
        // Remount per target so the form's defaultValues + internal state reset
        // cleanly between "create", "edit Oncology", "edit Cardiology".
        key={formState?.mode === "edit" ? formState.ward.id : "create"}
        mode={formState?.mode ?? "create"}
        open={formState !== null}
        defaultValues={
          formState?.mode === "edit"
            ? { name: formState.ward.name, shortCode: formState.ward.shortCode }
            : EMPTY_WARD
        }
        onSubmit={onSubmitForm}
        onOpenChange={(open) => {
          if (!open) setFormState(null);
        }}
        serverError={saveError}
      />

      <ArchiveWardDialog
        ward={archiveTarget}
        archiving={archiving}
        error={archiveError}
        onConfirm={() => void onConfirmArchive()}
        onOpenChange={(open) => {
          if (!open) setArchiveTarget(null);
        }}
      />
    </section>
  );
}

function WardList({
  wards,
  showArchived,
  onEdit,
  onArchive,
}: {
  wards: AdminWardDto[];
  showArchived: boolean;
  onEdit: (ward: AdminWardDto) => void;
  onArchive: (ward: AdminWardDto) => void;
}): JSX.Element {
  const visible = showArchived ? wards : wards.filter((w) => w.archivedAt === null);

  if (visible.length === 0) {
    return (
      <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
        {wards.length === 0
          ? "No wards yet. Click “Add ward” to create the first one."
          : "No active wards. Tick “Show archived wards” to see archived ones."}
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {visible.map((ward) => {
        const archived = ward.archivedAt !== null;
        return (
          <li
            key={ward.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-4"
          >
            <span className="font-medium">{ward.name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {ward.shortCode}
            </span>
            {archived && (
              <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                Archived
              </span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {!archived && (
                <Button variant="ghost" size="sm" asChild>
                  <Link
                    to={`/admin/wards/${ward.id}/members`}
                    aria-label={`Manage ${ward.name} team`}
                  >
                    <Users className="h-4 w-4" aria-hidden />
                    Team
                  </Link>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(ward)}
                aria-label={`Edit ${ward.name}`}
              >
                <Pencil className="h-4 w-4" aria-hidden />
                Edit
              </Button>
              {!archived && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onArchive(ward)}
                  aria-label={`Archive ${ward.name}`}
                >
                  Archive
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
